import { useState } from "react";
import {
    useFlyerTypes,
    useDeleteFlyerType,
    type CampaignFlyer,
} from "../api/campaigns";
import { FlyerTypeForm } from "./FlyerTypeForm";
import { showError, confirmAction } from "../shared/toast";
import "./flyer-types-panel.css";

interface FlyerTypesPanelProps {
    campaignId: string;
    campaignName: string;
}

export const FlyerTypesPanel = ({
    campaignId,
    campaignName,
}: FlyerTypesPanelProps): JSX.Element => {
    const [editingFlyerType, setEditingFlyerType] =
        useState<CampaignFlyer | null>(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const { data: campaignFlyers = [], isLoading, error } = useFlyerTypes(
        campaignId
    );
    const deleteMutation = useDeleteFlyerType(campaignId);

    const handleEdit = (campaignFlyer: CampaignFlyer) => {
        setEditingFlyerType(campaignFlyer);
        setShowCreateForm(false);
    };

    const handleDelete = async (id: string, flyerTypeName: string) => {
        const confirmed = await confirmAction(
            `Är du säker på att du vill ta bort flygbladet "${flyerTypeName}" från kampanjen?`
        );
        if (!confirmed) {
            return;
        }

        try {
            await deleteMutation.mutateAsync(id);
        } catch (error: any) {
            const message =
                error?.response?.data?.message ||
                "Kunde inte ta bort flygbladet. Kontrollera att det inte används i assignments.";
            showError("Kunde inte ta bort flygbladet", message);
        }
    };

    const handleFormSuccess = () => {
        setEditingFlyerType(null);
        setShowCreateForm(false);
    };

    const handleFormCancel = () => {
        setEditingFlyerType(null);
        setShowCreateForm(false);
    };

    if (error) {
        return (
            <div className="flyer-types-panel">
                <h3>Flygblad för {campaignName}</h3>
                <p className="error-message">Kunde inte ladda flygblad.</p>
            </div>
        );
    }

    return (
        <section
            className="flyer-types-panel"
            aria-label={`Flygblad för ${campaignName}`}
        >
            <div className="flyer-types-panel__header">
                <h3>Flygblad för {campaignName}</h3>
                <button
                    className="button button--primary"
                    onClick={() => {
                        setShowCreateForm(true);
                        setEditingFlyerType(null);
                    }}
                    aria-label="Lägg till flygblad"
                >
                    + Lägg till flygblad
                </button>
            </div>

            {(showCreateForm || editingFlyerType) && (
                <div className="flyer-types-panel__form">
                    <FlyerTypeForm
                        campaignId={campaignId}
                        campaignFlyer={editingFlyerType || undefined}
                        onSuccess={handleFormSuccess}
                        onCancel={handleFormCancel}
                    />
                </div>
            )}

            {isLoading ? (
                <p>Laddar...</p>
            ) : (
                <div className="flyer-types-panel__list">
                    {campaignFlyers.length === 0 ? (
                        <p>Inga flygblad har lagts till för denna kampanj.</p>
                    ) : (
                        <table className="flyer-types-table">
                            <thead>
                                <tr>
                                    <th>Flygblad</th>
                                    <th>Standard</th>
                                    <th>Prioritet</th>
                                    <th>Planerat antal</th>
                                    <th>Åtgärder</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campaignFlyers.map((cf) => {
                                    const displayName = cf.name ?? "Okänt flygblad";
                                    const displaySummary = cf.summary;

                                    return (
                                    <tr key={cf.id}>
                                        <td>
                                            <div>
                                                <strong>{displayName}</strong>
                                                {displaySummary && (
                                                    <small>{displaySummary}</small>
                                                )}
                                            </div>
                                        </td>
                                        <td>{cf.isDefault ? "Ja" : "Nej"}</td>
                                        <td>{cf.priority ?? "-"}</td>
                                        <td>
                                            {cf.plannedQuantity !== null && cf.plannedQuantity !== undefined
                                                ? cf.plannedQuantity.toLocaleString("sv-SE")
                                                : "-"}
                                        </td>
                                        <td>
                                            <div className="flyer-types-table__actions">
                                                <button
                                                    className="button button--secondary"
                                                    onClick={() => handleEdit(cf)}
                                                    aria-label={`Redigera ${displayName}`}
                                                >
                                                    Redigera
                                                </button>
                                                <button
                                                    className="button button--danger"
                                                    onClick={() =>
                                                        handleDelete(cf.id, displayName)
                                                    }
                                                    disabled={deleteMutation.isPending}
                                                    aria-label={`Ta bort ${displayName}`}
                                                >
                                                    {deleteMutation.isPending ? "Tar bort..." : "Ta bort"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </section>
    );
};